// DesignStep — the fork that lives at the "Choose A Design" step of the
// wizard. Two tabs: "🎨 Choose a template" (default) + "✨ Let AI design".
// Tab state is local; switching preserves all upstream wizard state
// (product, name, etc.) because the steps above this don't unmount.
//
// The parent owns the *consequence* of the fork (which sections render
// downstream, which Generate path fires, whether Step 8 is trimmed) —
// this component is purely the picker UI + content slots.

import { useEffect } from 'react';

export type DesignMode = 'ai' | 'template';

interface DesignStepProps {
  /** Current mode — controlled by parent so the wizard's downstream logic
   *  can read it (e.g. trim the Text step when 'ai'). */
  mode: DesignMode;
  onModeChange: (mode: DesignMode) => void;
  /** AI tab content — typically <AiGeneratePanel ... />. */
  aiContent: React.ReactNode;
  /** Template tab content — typically the existing StepBackground /
   *  StepSocialDesign / etc. component. */
  templateContent: React.ReactNode;
  /** Optional short copy shown under the tab bar to set context. */
  description?: string;
}

export default function DesignStep({
  mode,
  onModeChange,
  aiContent,
  templateContent,
  description,
}: DesignStepProps) {
  // Default to template mode on first mount if no explicit selection has been
  // made. Parent owns the state so we just signal the default upward.
  useEffect(() => {
    if (mode !== 'ai' && mode !== 'template') {
      onModeChange('template');
    }
    // Run once on mount; mode changes after are user-driven.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4">
      {/* Tab bar — pill-style, matches the picker tabs used elsewhere in
          the wizard (StepUpload's library/url tabs). Active tab gets
          solid primary; inactive gets a quiet surface treatment. The
          default ("Choose a template") renders first so the visually-
          first tab matches the default-selected tab. */}
      <div className="flex gap-2 p-1 rounded-full bg-surface-dark/15 w-full sm:w-auto sm:inline-flex">
        <TabPill
          active={mode === 'template'}
          onClick={() => onModeChange('template')}
          icon="ri-palette-line"
          label="Choose a template"
        />
        <TabPill
          active={mode === 'ai'}
          onClick={() => onModeChange('ai')}
          icon="ri-sparkling-2-line"
          label="Let AI design"
        />
      </div>

      {description && (
        <p className="text-sm text-dark-muted">{description}</p>
      )}

      {/* Active content. We keep BOTH children mounted via `hidden` instead
          of conditionally rendering — preserves form state if the user
          toggles back and forth.
          Fixed-height container (h-[520px]) so the two tabs feel like the
          same physical surface — Template's grid is naturally tall and
          scrolls inside; AI's content is short and sits at the top.
          The fixed height also gives the loading skeleton (rendered by
          the terminal step after Generate) a consistent visual anchor. */}
      <div className="relative h-[520px]">
        <div
          className={`absolute inset-0 overflow-y-auto pr-1 ${mode === 'ai' ? '' : 'hidden'}`}
          aria-hidden={mode !== 'ai'}
        >
          {aiContent}
        </div>
        <div
          className={`absolute inset-0 overflow-y-auto pr-1 ${mode === 'template' ? '' : 'hidden'}`}
          aria-hidden={mode !== 'template'}
        >
          {templateContent}
        </div>
      </div>
    </div>
  );
}

function TabPill({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: string;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition-all cursor-pointer ${
        active
          ? 'bg-primary text-white shadow-sm shadow-primary/25'
          : 'bg-transparent text-dark-muted hover:text-dark hover:bg-white/50'
      }`}
      aria-pressed={active}
    >
      <i className={icon} /> {label}
    </button>
  );
}
